import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component
} from '@angular/core';
import { DateTime } from 'luxon';
import { Subscription } from 'rxjs';
import { GridActionColumnActions } from 'src/app/interfaces/grid-action-column-actions';
import { GridActionColumnMap } from 'src/app/interfaces/grid-action-column-map';
import { CompanyVendorService } from 'src/app/services/company-vendor.service';
import { SharedDataService } from 'src/app/services/shared-data.service';
import { ToastService } from 'src/app/services/toast.service';
import { VendorService } from 'src/app/services/vendor.service';

@Component({
  selector: 'app-admin-vendor-clients',
  templateUrl: './admin-vendor-clients.component.html',
  styleUrls: ['./admin-vendor-clients.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AdminVendorClientsComponent {
  constructor(
    private SharedDataService: SharedDataService,
    private VendorService: VendorService,
    private ToastService: ToastService,
    private cdr: ChangeDetectorRef
  ) {
    this.SharedDataServiceSubscription =
      this.SharedDataService.listenForEvent<number>().subscribe({
        next: ({ eventName, data }) => {
          if (eventName === 'SetVendorId' && typeof data === 'number') {
            this.VendorId = data;

            Object.assign(this.SuppliedQueryData, { VendorId: this.VendorId });

            this.VendorService.getVendorV2(this.VendorId, {
              // VPDW-4240: renewalDate is the LAST COMPLETED information
              // renewal (null until the vendor's first one) and createdAt is the
              // fallback anchor the backend uses when it is null.
              attributes: JSON.stringify([
                'id',
                'legalName',
                'renewalDate',
                'createdAt'
              ])
            }).subscribe({
              next: (Vendor) => {
                const { legalName, renewalDate, createdAt } = Vendor;

                if (!legalName) {
                  throw this.ToastService.addErrorToast(
                    'Error',
                    'Vendor is missing legal name.',
                    { returnError: true }
                  );
                }

                this.SharedDataService.sendEvent(
                  'SetTitle',
                  `Clients - ${legalName}`
                );

                this.setInfoRenewalDates(renewalDate, createdAt);

                // Zoneless: this runs in an RxJS callback, so the new dates
                // would not render without an explicit check.
                this.cdr.markForCheck();
              }
            });
          }
        }
      });

    this.SharedDataService.sendEvent('GetVendorId');
  }

  VendorId?: number;
  ColumnIdToColumnActions: GridActionColumnMap = {
    ['$actionColumn']: ['edit']
  };

  // VPDW-4240: the vendor-level information-renewal clock, shown above the grid.
  LastInfoRenewalDate?: Date | null;
  InfoRenewalDueDate?: Date | null;
  InfoRenewalChipLabel: string | null = null;
  InfoRenewalChipClass: string = 'chip-warning';

  // Widest of the backend's info-renewal reminder tiers (60 / 30 / 7 days
  // before due — vive-v2 communications/vendor-info-renewal-upcoming-routing.ts),
  // so the chip turns amber the same day the vendor's first reminder goes out.
  private static readonly INFO_RENEWAL_DUE_SOON_DAYS = 60;

  /**
   * Derives the information-renewal due date from the same anchor the backend
   * uses: the last completed renewal, else the vendor's signup date, plus one
   * calendar year.
   *
   * Luxon rather than `setFullYear(+1)` on purpose. Both are "plus a year", but
   * on a Feb-29 anchor `setFullYear` rolls over to Mar 1 while Luxon clamps to
   * Feb 28 — and Feb 28 is what MySQL's `INTERVAL 1 YEAR` produces in the
   * `infoRenewalDueDate` alias. Matching the alias keeps this card and the
   * compliance card showing the same date for the same vendor.
   */
  private setInfoRenewalDates(
    renewalDate?: Date | null,
    createdAt?: Date | null
  ): void {
    this.LastInfoRenewalDate = renewalDate ?? null;

    const anchor = renewalDate ?? createdAt;

    if (!anchor) {
      this.InfoRenewalDueDate = null;
      this.InfoRenewalChipLabel = null;

      return;
    }

    const dueDate = DateTime.fromJSDate(new Date(anchor)).plus({ years: 1 });

    this.InfoRenewalDueDate = dueDate.toJSDate();

    // Compare calendar days so a date due later today doesn't read as overdue.
    const days = Math.round(
      dueDate.startOf('day').diff(DateTime.now().startOf('day'), 'days').days
    );

    this.InfoRenewalChipClass = days < 0 ? 'chip-error' : 'chip-warning';

    if (days < 0) {
      const overdueBy = Math.abs(days);
      this.InfoRenewalChipLabel = `Overdue ${overdueBy} ${overdueBy === 1 ? 'day' : 'days'}`;
    } else if (days === 0) {
      this.InfoRenewalChipLabel = 'Due today';
    } else if (days <= AdminVendorClientsComponent.INFO_RENEWAL_DUE_SOON_DAYS) {
      this.InfoRenewalChipLabel = `Due in ${days} ${days === 1 ? 'day' : 'days'}`;
    } else {
      // Current — deliberately no chip (docs/design-components.md: don't pill a
      // passing state).
      this.InfoRenewalChipLabel = null;
    }
  }

  SharedDataServiceSubscription?: Subscription;

  SuppliedQueryData: { [key: string]: { [key: string]: any } } = {};

  onActionEvent({ action }: { action: GridActionColumnActions }) {
    if (action === 'edit') {
      // Need to display modal. Confirm the modal is accurate with the team.
    }
  }

  ngOnDestroy(): void {
    if (this.SharedDataServiceSubscription) {
      this.SharedDataServiceSubscription.unsubscribe();
    }
  }
}
